import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, Pipe, PipeTransform, ViewEncapsulation, HostBinding } from '@angular/core';
import { SideDrawerComponent } from '../side-drawer-container/side-drawer/side-drawer.component';
import { SideNavService } from './side-nav.service';
import { Item } from '../listbox/listbox.component';
import { ListFilterType } from '../list/directives/list-filter.directive';
import { AppConfigService } from '@app/services';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { StateService } from '@app/core/services/state.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { ComponentBaseComponent } from '../component-base/component-base.component';
import { debounceTime, Subject } from 'rxjs';

@Component({
    selector: 'tt-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent extends ComponentBaseComponent implements OnInit, OnDestroy {
    public filter = '';
    private _filter = '';
    private mobileMenuTouch?: { startY: number; lastY: number; dragging: boolean };

    public $remember = new Subject<string>();

    constructor(
        public sideNav: SideNavService,
        public appConfig: AppConfigService,
        public appSettings: AppSettingsService,
        public graphAuth: GraphAuthService,
        public state: StateService,
        private layout: LayoutService,
        private cdr: ChangeDetectorRef,
        private modal: ModalService,
        private remember: RememberService,
    ) {
        super();
        this.sideNav.open$.subscribe({
            next: (value) => {
                if (value) {
                    this.filter = this._filter;
                } else {
                    this._filter = this.filter;
                    this.filter = '';
                }
            },
        });
        this.$remember.pipe(debounceTime(500)).subscribe({ next: (value) => this.remember.remember('tt_sidenav.filter', value) });
    }

    public trackBy = (_: number, item: any) => {
        return item.item_id;
    };

    public openFavouritesDialog() {
        this.modal.openFavouritesDialog();
    }

    public afterClick() {
        if (!this.layout.getMediaQueries().tablet.matches) {
            this.sideNav.open$.next(false);
        }
    }

    public onMobileMenuTouchStart(event: TouchEvent): void {
        const touch = event.touches.length === 1 ? event.touches[0] : undefined;
        this.mobileMenuTouch = touch ? { startY: touch.clientY, lastY: touch.clientY, dragging: false } : undefined;
    }

    public onMobileMenuTouchMove(event: TouchEvent): void {
        const list = event.currentTarget as HTMLElement | null;
        const touch = event.touches[0];

        if (!list || !touch || !this.mobileMenuTouch) return;

        if (!this.mobileMenuTouch.dragging && Math.abs(this.mobileMenuTouch.startY - touch.clientY) < 5) return;

        const deltaY = this.mobileMenuTouch.lastY - touch.clientY;
        if (deltaY === 0) return;

        this.mobileMenuTouch.dragging = true;
        list.scrollTop += deltaY;
        this.mobileMenuTouch.lastY = touch.clientY;

        if (event.cancelable) event.preventDefault();
    }

    public onMobileMenuTouchEnd(): void {
        this.mobileMenuTouch = undefined;
    }

    public saveExpanded() {
        this.remember.remember(
            'tt_sidenav.itemsexpanded',
            this.base64UrlEncodeUnicode(
                JSON.stringify(
                    this.sideNav.rootSideNavItems$
                        .getValue()
                        .filter((item) => item.expanded === true)
                        .map((item) => item.item_id),
                ),
            ),
        );
    }

    public async rememberFilter(value: string) {
        this.$remember.next(value);
    }

    private async loadFilter() {
        const filter = (await this.remember.getLastStatus('tt_sidenav.filter'))[0].variablevalue;

        if (this.sideNav.open$.getValue()) {
            this.filter = filter;
            this._filter = '';
        } else {
            this.filter = '';
            this._filter = filter;
        }
    }

    async ngOnInit() {
        await this.sideNav.init();
        this.cdr.markForCheck();
        await this.loadFilter();
        this.cdr.markForCheck();

        this.layout.getMediaQueries().tablet.addEventListener('change', this.cdr.markForCheck.bind(this.cdr));
    }

    ngOnDestroy(): void {
        this.layout.getMediaQueries().tablet.removeEventListener('change', this.cdr.markForCheck.bind(this.cdr));
    }
}

@Pipe({
    name: 'sideNavFilter',
})
export class SideNavFilterPipe<TType extends Item> implements PipeTransform {
    constructor(private sideNav: SideNavService) {}
    /**
     * Filters through the given list of items using the given filter.
     * If strict is not set then it matches with items containing all of space seperated words, if strict is `true` then it will only match with items containing the phrase as a whole.
     *
     * @param items the list of items to filter through.
     * @param filter the filter to apply to the items.
     * @param filterType the behavior of the filter, default is 'fulltext'.
     * @returns list of filtered items.
     */
    transform(items: TType[], filter?: string, filterType?: ListFilterType): TType[] {
        if (!filter) return items;

        return items.filter((item: TType) => this.findMatch(item, filter, filterType) || this.sideNav.getChildren(item.item_id!, this.sideNav.allSideNavItems$.getValue()).some((i) => this.findMatch(i as any, filter, filterType)));
    }

    private findMatch(item: TType, filter: string, filterType?: ListFilterType) {
        let words: string[] = [];
        let match = false;

        if (filterType === 'phrasetext') {
            words = [filter];
        } else {
            words = filter.split(' ');
        }

        let filtervalue = '';

        if (Object.hasOwn(item, 'item_filtervalue')) {
            filtervalue = item.item_filtervalue!.toLowerCase();
        } else {
            filtervalue = Object.values(item)
                .map((val) => '' + val)
                .reduce((acc, value) => acc + ' ' + value, '')
                .toLowerCase();
        }

        if (filterType === 'freetext') {
            match = words.some((word) => filtervalue.indexOf(word.toLowerCase()) !== -1 || item.item_id === '0' || item.item_id === '-1');
        } else {
            match = words.every((word) => filtervalue.indexOf(word.toLowerCase()) !== -1 || item.item_id === '0' || item.item_id === '-1');
        }

        return !!match;
    }
}
